// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Re-analyze flights from the Aircraft workspace:
// - reanalyzeFlight: the per-row Actions button. POSTs to
//   /api/flights/{id}/reanalyze, which re-runs the analyzer over the existing
//   normalized artifact and rebuilds the evidence packet. Existing AI
//   summaries become stale and must be regenerated for the new evidence
//   packet, which may count toward the user's monthly AI request allowance.
// - reanalyzeAllFlights: the flights-panel header bulk action (issue 151) —
//   the operator remedy after an ANALYSIS_VERSION bump marks stored runs
//   stale. Flights the server already flags "analysis update available" are
//   queued in ONE request through /api/aircraft/{id}/analysis-upgrades and
//   drained by the worker pool while the client polls the batch; anything
//   else (a forced re-run of a current flight) keeps the serial per-flight
//   path. Progress is surfaced through the shared upload work notice with
//   per-flight Retry on failures.

import { waitForUploadIngestJob } from "../persistence/upload-ingest-poller.js";

const BULK_UPGRADE_STALL_TIMEOUT_MS = 15 * 60_000;

function bulkUpgradeEligible(entry) {
  return Boolean(entry?.database?.aircraftId)
    && entry?.database?.compatibilityStatus?.status === "analysis_update_available";
}

// Shape a completed durable job into the payload postFlightReanalyze returns, so
// the bulk path applies the exact same entry updates as the per-flight path.
function reanalyzePayloadFromCompletedJob(completedJob, basePayload = {}) {
  return {
    ...basePayload,
    ingestJob: completedJob,
    analysisRun: completedJob?.summary_json?.analysisRunId
      ? { id: completedJob.summary_json.analysisRunId }
      : null,
    evidencePacket: completedJob?.summary_json?.evidencePacketId
      ? { id: completedJob.summary_json.evidencePacketId }
      : null,
    compatibilityStatus: completedJob?.summary_json?.compatibilityStatus
      ?? basePayload.compatibilityStatus,
    ...(Object.prototype.hasOwnProperty.call(completedJob?.summary_json ?? {}, "flightFlags")
      ? { flagAnalysis: completedJob.summary_json.flightFlags }
      : {}),
  };
}

export function createFlightReanalyze({
  flightDisplayTitle,
  renderAircraftFlightsPanel,
  isAircraftSelected = () => true,
  invalidateFlightCaches = () => {},
  // Bulk batch polling cadence; tests inject a no-op wait.
  bulkPollIntervalMs = 1000,
  bulkPollWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  confirmReanalyzeFlight = () => Promise.resolve(true),
  confirmReanalyzeAllFlights = () => Promise.resolve(true),
  isSupportedFlight = () => true,
  appReadOnlyMode = () => false,
  readOnlyActionMessage = (action) => `${action} is unavailable in this preview.`,
  showReanalyzeProgressNotice = null,
  onFlightReanalyzed = () => {},
  openFlightAiSummary = async () => {},
}) {
  function renderReanalysisAircraftPanel(aircraft) {
    if (isAircraftSelected(aircraft)) renderAircraftFlightsPanel(aircraft);
  }

  async function postFlightReanalyze(entry, { onJobStatus = () => {} } = {}) {
    const flightId = entry?.database?.flightId;
    const response = await fetch(`/api/flights/${encodeURIComponent(flightId)}/reanalyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error || `Re-analysis failed with status ${response.status}.`);
    }
    let payload = await response.json().catch(() => ({}));
    if (payload?.ingestJob?.id) {
      const completedJob = await waitForUploadIngestJob(payload.ingestJob.id, {
        fetchJob: (jobId) => fetch(`/api/jobs/${encodeURIComponent(jobId)}`),
        onStatus: onJobStatus,
        stallTimeoutMs: BULK_UPGRADE_STALL_TIMEOUT_MS,
        intervalMs: 500,
      });
      payload = reanalyzePayloadFromCompletedJob(completedJob, payload);
    }
    applyReanalyzePayload(entry, payload);
    return payload;
  }

  function applyReanalyzePayload(entry, payload) {
    if (payload?.analysisRun?.id && entry.database) {
      entry.database.analysisRunId = payload.analysisRun.id;
      // The row can render its new CHT/EGT result immediately after the job
      // completes. If an older server omits this compact payload, discard the
      // prior run's inline flags so they cannot be cached under the new run id.
      if (Object.prototype.hasOwnProperty.call(payload, "flagAnalysis")) {
        entry.database.flagAnalysis = payload.flagAnalysis;
      } else {
        delete entry.database.flagAnalysis;
      }
    }
    if (payload?.compatibilityStatus && entry.database) {
      entry.database.compatibilityStatus = payload.compatibilityStatus;
    }
    if (payload?.evidencePacket?.id && entry.database) {
      entry.database.currentEvidencePacketId = payload.evidencePacket.id;
    }
    invalidateFlightCaches(entry);
  }

  async function reanalyzeFlight(entry, button = null) {
    const flightId = entry?.database?.flightId;
    if (!flightId) {
      window.alert("Cannot re-analyze: this flight is not backed by the database.");
      return;
    }
    const actionLabel = String(button?.dataset?.aircraftFlightReanalyzeLabel ?? "").trim() || "Re-analyze";
    const progressLabel = actionLabel === "Update"
      ? "Updating"
      : actionLabel.startsWith("Analyze") ? "Analyzing" : "Re-analyzing";
    // The button stays enabled for an already-current analysis so a forced
    // re-run is possible; the dialog says so and relabels its confirm.
    const upToDate = button?.dataset?.flightUpToDate === "1";
    // The flight/log label stays out of the title (it made the heading
    // unreadable); the dialog body identifies the flight by its source file.
    const confirmed = await confirmReanalyzeFlight({
      title: upToDate ? "Force re-process this flight?" : `${actionLabel} this flight?`,
      entry,
      upToDate,
    });
    if (!confirmed) return;
    // The dialog can carry a "generate AI summary afterwards" opt-in
    // (object result); plain boolean true keeps the legacy shape.
    const generateAiSummary = confirmed?.generateAiSummary === true;
    let originalLabel = "";
    let originalText = "";
    let reanalyzed = false;
    try {
      if (button) {
        originalLabel = button.getAttribute("aria-label") ?? "";
        const label = button.querySelector("span");
        // Only capture/restore text for actual text-label buttons. The
        // icon-only Re-analyze button's first span is the icon wrapper, and
        // writing its whitespace textContent back would wipe out the SVG,
        // leaving an empty (invisible) button after the run.
        originalText = actionLabel === "Update" ? label?.textContent ?? "" : "";
        if (label && actionLabel === "Update") label.textContent = "Updating...";
        button.disabled = true;
        button.classList.add("is-generating");
        button.setAttribute("aria-label", `${progressLabel} ${flightDisplayTitle(entry)}`);
      }
      const payload = await postFlightReanalyze(entry);
      reanalyzed = true;
      await onFlightReanalyzed(entry, payload);
    } catch (error) {
      console.error("Flight re-analysis failed", error);
      window.alert(`Could not re-analyze flight: ${error.message}`);
    } finally {
      if (button) {
        const label = button.querySelector("span");
        if (label && originalText) label.textContent = originalText;
        button.disabled = false;
        button.classList.remove("is-generating");
        if (originalLabel) button.setAttribute("aria-label", originalLabel);
      }
      renderReanalysisAircraftPanel(entry.aircraft);
    }
    if (reanalyzed && generateAiSummary) {
      try {
        await openFlightAiSummary(entry);
      } catch (error) {
        console.error("Post-reanalyze AI summary failed", error);
      }
    }
  }

  let bulkRun = null;

  function reanalyzeAllFlightsBusy() {
    return bulkRun !== null;
  }

  function reanalyzeAllFlightsStatus() {
    return {
      active: bulkRun !== null,
      aircraft: bulkRun?.aircraft ?? null,
    };
  }

  // Keep the notice membership identical to the confirmed batch. The shared
  // progress list is scrollable and patches rows in place, so every flight can
  // remain reachable while its status changes without disturbing scroll.
  function visibleReanalyzeJobs(jobs) {
    return jobs.map(({ entry, ...job }) => job);
  }

  async function reanalyzeAllFlights(aircraft, entries, { scope = "all", skipConfirmation = false } = {}) {
    if (bulkRun) return { started: false, reason: "busy" };
    if (appReadOnlyMode()) {
      window.alert(readOnlyActionMessage("Re-analyzing flights"));
      return { started: false, reason: "read-only" };
    }
    const targets = (Array.isArray(entries) ? entries : [])
      .filter((entry) => entry?.database?.flightId && isSupportedFlight(entry) !== false);
    if (!targets.length) return { started: false, reason: "no-flights" };
    const confirmed = skipConfirmation
      ? true
      : await confirmReanalyzeAllFlights({ count: targets.length, aircraft, scope });
    if (!confirmed) return { started: false, reason: "cancelled" };
    // The confirmation dialog yields control. Re-check so two near-simultaneous
    // confirmations cannot both claim the single client-side progress queue.
    if (bulkRun) return { started: false, reason: "busy" };

    const runState = { aircraft };
    bulkRun = runState;
    const jobs = targets.map((entry) => ({
      id: `reanalyze-${entry.database.flightId}`,
      entry,
      fileName: flightDisplayTitle(entry),
      status: "queued",
      statusLabel: "Queued",
      message: "Waiting to re-analyze",
    }));
    const counts = () => ({
      done: jobs.filter((job) => job.status === "ready").length,
      failed: jobs.filter((job) => job.status === "error").length,
    });
    const sync = ({ status, message, autoDismissMs = 0 }) => {
      const { done, failed } = counts();
      const settled = done + failed;
      showReanalyzeProgressNotice?.({
        status,
        title: settled >= jobs.length
          ? failed
            ? `Re-analysis finished: ${done} of ${jobs.length} flight${jobs.length === 1 ? "" : "s"}`
            : `Re-analyzed ${done} flight${done === 1 ? "" : "s"}`
          : `Re-analyzing flights (${Math.min(jobs.length, settled + 1)} of ${jobs.length})`,
        message,
        jobs: visibleReanalyzeJobs(jobs),
        actionLabel: "",
        autoDismissMs,
        onRetryJob: retryReanalyzeJob,
      });
    };

    async function runReanalyzeJob(job) {
      job.status = "analyzing";
      job.statusLabel = "Analyzing";
      job.message = "Re-analyzing flight";
      job.error = null;
      sync({ status: "loading", message: `Analyzing ${job.fileName}.` });
      try {
        await postFlightReanalyze(job.entry, {
          onJobStatus: (serverJob) => {
            const status = String(serverJob?.status ?? "queued");
            job.statusLabel = status === "processing" ? "Analyzing" : status === "queued" ? "Queued" : job.statusLabel;
            job.message = status === "processing" ? "Analysis upgrade is running" : "Waiting for an analysis worker";
            sync({ status: "loading", message: `${job.statusLabel}: ${job.fileName}.` });
          },
        });
        job.status = "ready";
        job.statusLabel = "Analyzed";
        job.message = `${job.fileName} analyzed.`;
      } catch (error) {
        console.error("Flight re-analysis failed", error);
        job.status = "error";
        job.statusLabel = "Failed";
        job.message = "Re-analysis failed";
        job.error = error.message;
      }
      renderReanalysisAircraftPanel(aircraft);
    }

    function finishNotice() {
      const { done, failed } = counts();
      sync({
        status: failed ? "error" : "ready",
        message: failed
          ? `${failed} flight${failed === 1 ? "" : "s"} failed — use Retry on a row to run it again.`
          : "All flights are analyzed against the current analysis version.",
        autoDismissMs: failed ? 0 : 12000,
      });
      return { started: true, analyzed: done, failed };
    }

    async function retryReanalyzeJob(jobId) {
      const job = jobs.find((item) => item.id === jobId);
      if (!job || job.status !== "error" || bulkRun) return;
      const retryState = { aircraft };
      bulkRun = retryState;
      try {
        await runReanalyzeJob(job);
      } finally {
        if (bulkRun === retryState) bulkRun = null;
        finishNotice();
        renderReanalysisAircraftPanel(aircraft);
      }
    }

    function markJobFailed(job, message) {
      job.status = "error";
      job.statusLabel = "Failed";
      job.message = "Re-analysis failed";
      job.error = message;
    }

    function markJobReady(job) {
      job.status = "ready";
      job.statusLabel = "Analyzed";
      job.message = `${job.fileName} analyzed.`;
      job.error = null;
    }

    // Queue every eligible flight of one aircraft in a single request, then poll the batch.
    // Jobs the server declines to queue (it no longer sees an update for them) fall back to the
    // serial per-flight path so nothing the user selected is silently skipped.
    async function runBulkUpgradeJobs(bulkJobs) {
      const aircraftId = bulkJobs[0].entry.database.aircraftId;
      for (const job of bulkJobs) {
        job.status = "analyzing";
        job.statusLabel = "Queued";
        job.message = "Waiting for an analysis worker";
        job.error = null;
      }
      sync({ status: "loading", message: `Queueing ${bulkJobs.length} flight${bulkJobs.length === 1 ? "" : "s"}.` });
      let batch = null;
      try {
        const response = await fetch(`/api/aircraft/${encodeURIComponent(aircraftId)}/analysis-upgrades`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scope: "selected",
            flightIds: bulkJobs.map((job) => job.entry.database.flightId),
          }),
        });
        batch = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(batch?.error || `Re-analysis failed with status ${response.status}.`);
      } catch (error) {
        console.error("Bulk re-analysis queueing failed", error);
        for (const job of bulkJobs) markJobFailed(job, error.message);
        renderReanalysisAircraftPanel(aircraft);
        return;
      }
      const serverJobsByFlightId = new Map(
        (Array.isArray(batch?.jobs) ? batch.jobs : []).map((serverJob) => [String(serverJob?.flight_id ?? ""), serverJob]),
      );
      const pending = new Map();
      const notQueued = [];
      for (const job of bulkJobs) {
        const serverJob = serverJobsByFlightId.get(String(job.entry.database.flightId));
        if (serverJob?.id) pending.set(String(serverJob.id), job);
        else notQueued.push(job);
      }
      if (pending.size) {
        await pollBulkUpgradeBatch(aircraftId, batch?.batchId ?? null, pending);
      }
      for (const job of notQueued) {
        await runReanalyzeJob(job);
      }
    }

    async function pollBulkUpgradeBatch(aircraftId, batchId, pending) {
      const total = pending.size;
      const query = new URLSearchParams({ limit: String(total) });
      if (batchId) query.set("batchId", batchId);
      const settle = (serverJob) => {
        const job = pending.get(String(serverJob?.id ?? ""));
        if (!job) return;
        const status = String(serverJob?.status ?? "queued");
        if (status === "completed") {
          applyReanalyzePayload(job.entry, reanalyzePayloadFromCompletedJob(serverJob));
          markJobReady(job);
          pending.delete(String(serverJob.id));
          renderReanalysisAircraftPanel(aircraft);
        } else if (status === "failed") {
          console.error("Flight re-analysis failed", serverJob?.error_message);
          markJobFailed(job, serverJob?.error_message || "Re-analysis failed.");
          pending.delete(String(serverJob.id));
          renderReanalysisAircraftPanel(aircraft);
        } else {
          job.statusLabel = status === "processing" ? "Analyzing" : "Queued";
          job.message = status === "processing" ? "Analysis upgrade is running" : "Waiting for an analysis worker";
        }
      };
      let consecutiveFetchFailures = 0;
      let lastProgressAt = Date.now();
      let lastSignature = "";
      while (pending.size) {
        let statusPayload = null;
        try {
          const response = await fetch(`/api/aircraft/${encodeURIComponent(aircraftId)}/analysis-upgrades?${query}`);
          statusPayload = await response.json().catch(() => null);
          if (!response.ok) throw new Error(statusPayload?.error || `Could not read re-analysis batch (${response.status}).`);
          consecutiveFetchFailures = 0;
        } catch (error) {
          consecutiveFetchFailures += 1;
          if (consecutiveFetchFailures >= 5) {
            for (const job of pending.values()) markJobFailed(job, `Lost connection while waiting for re-analysis: ${error?.message ?? error}`);
            pending.clear();
            renderReanalysisAircraftPanel(aircraft);
            return;
          }
          await bulkPollWait(Math.max(bulkPollIntervalMs, 1000));
          continue;
        }
        const serverJobs = Array.isArray(statusPayload?.status?.jobs) ? statusPayload.status.jobs : [];
        for (const serverJob of serverJobs) settle(serverJob);
        const signature = serverJobs
          .map((serverJob) => `${serverJob?.id}:${serverJob?.status}:${serverJob?.current_step ?? ""}`)
          .sort()
          .join("|");
        if (signature !== lastSignature) {
          lastSignature = signature;
          lastProgressAt = Date.now();
        } else if (Date.now() - lastProgressAt > BULK_UPGRADE_STALL_TIMEOUT_MS) {
          const seconds = Math.round(BULK_UPGRADE_STALL_TIMEOUT_MS / 1000);
          for (const job of pending.values()) markJobFailed(job, `Re-analysis has been stuck for ${seconds}s. Try refreshing the flight list.`);
          pending.clear();
          renderReanalysisAircraftPanel(aircraft);
          return;
        }
        const { done, failed } = counts();
        sync({ status: "loading", message: `${done + failed} of ${total} flight${total === 1 ? "" : "s"} settled.` });
        if (pending.size) await bulkPollWait(bulkPollIntervalMs);
      }
    }

    try {
      // Flights the server already flags as update-available go through the bulk queue (one
      // request, worker-pool drained); anything else keeps the serial per-flight path, which
      // re-analyzes regardless of status.
      const bulkJobs = jobs.filter((job) => bulkUpgradeEligible(job.entry));
      const bulkByAircraft = new Map();
      for (const job of bulkJobs) {
        const aircraftId = job.entry.database.aircraftId;
        if (!bulkByAircraft.has(aircraftId)) bulkByAircraft.set(aircraftId, []);
        bulkByAircraft.get(aircraftId).push(job);
      }
      for (const group of bulkByAircraft.values()) {
        await runBulkUpgradeJobs(group);
      }
      for (const job of jobs) {
        if (!bulkJobs.includes(job)) await runReanalyzeJob(job);
      }
      return finishNotice();
    } finally {
      if (bulkRun === runState) bulkRun = null;
      renderReanalysisAircraftPanel(aircraft);
    }
  }

  return {
    reanalyzeFlight,
    reanalyzeAllFlights,
    reanalyzeAllFlightsBusy,
    reanalyzeAllFlightsStatus,
  };
}
